import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';

import { format } from 'app/shared/utils/string.util';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { AzureBlobStorageLinkedService } from 'app/model/linkedservice/azure-blob-storage.linkedservice';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { StorageAccountKind } from 'app/apis/azure-storage-constants';
import { IParameterizableMetadata } from 'app/extensibility/metadatas/control/parameterizable-metadata';
import { Constants } from 'app/shared/shared-constants';
import { containerUriForm, storageUrlForm } from 'app/shared/constants/links.const';
import { PrimaryEndpoints } from 'app/extensibility/metadatas/control/azure-storage-account-select-metadata';

function sasUriBase(isTable: boolean, showFileShare?: boolean, isGen2?: boolean): ControlMetadata {
    return {
        type: 'textbox',
        bind: 'sasUri',
        label: stringResources.ModelName_SasUrl,
        required: true,
        isLabelVertical: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        placeholder: isTable ? `${stringResources.Sample}: https://myaccount.table.core.windows.net/mytable` :
            showFileShare ? `${stringResources.Sample}: https://myaccount.file.core.windows.net/` :
            isGen2 ? `${stringResources.Sample}: https://myaccount.dfs.core.windows.net/` :
                `${stringResources.Sample}: https://myaccount.blob.core.windows.net/sascontainer/sasblob.txt`,
        description: stringResources.AzureStorage_SasUrlDescription
    };
}

const storageAccountKindOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.None,
        value: undefined
    },
    {
        label: StorageAccountKind.GeneralPurposeV1,
        value: StorageAccountKind.GeneralPurposeV1
    },
    {
        label: StorageAccountKind.GeneralPurposeV2,
        value: StorageAccountKind.GeneralPurposeV2
    },
    {
        label: StorageAccountKind.BlobStorage,
        value: StorageAccountKind.BlobStorage
    },
    {
        label: StorageAccountKind.BlockBlobStorage,
        value: StorageAccountKind.BlockBlobStorage
    }
];

const storageUrl = (serviceType: PrimaryEndpoints) => {
    return format(storageUrlForm, serviceType);
};

const containerUriPlaceholder = format(containerUriForm, 'blob');

const serviceEndpointBase = (serviceType: PrimaryEndpoints): ControlMetadata => {
    const serviceTypeStr = serviceType.replace(/['"]+/g, '');
    return({
        type: 'textbox',
        bind: 'serviceEndpoint',
        label: stringResources.ModelName_ServiceEndpoint,
        required: true,
        placeholder: storageUrl(serviceType),
        description: stringResources.AzureStorage_blobEndpointDescription,
        validators: [
            { type: 'required', errorMessage: '' },
            {
                type: 'pattern',
                pattern: `^https://[^\/\.]+(\\.${serviceTypeStr}\\.(${Constants.storageValidEndpointSuffixes})|\\.z[0-9]+\\.${serviceTypeStr}\\.${Constants.partitionedDnsEndpointSuffix})/?$|^[*]{10}$`,
                errorMessage: format(stringResources.Validator_ErrorMessage_Pattern, storageUrl(serviceType)),
            },
            {
                type: 'pattern',
                pattern: `^https://[a-z0-9-]{3,}(\\.${serviceTypeStr}\\.(${Constants.storageValidEndpointSuffixes})|\\.z[0-9]+\\.${serviceTypeStr}\\.${Constants.partitionedDnsEndpointSuffix})/?$|^[*]{10}$`,
                errorMessage: stringResources.Validation_StorageAccountName,
            }
        ],
        isLabelVertical: true
    });
};

const containerUriBase: ControlMetadata = {
    type: 'textbox',
    bind: 'containerUri',
    label: stringResources.ModelName_ContainerUriName,
    required: true,
    placeholder: containerUriPlaceholder,
    description: stringResources.AzureStorage_ContainerUriDescription,
    validators: [
        { type: 'required', errorMessage: '' },
        {
            type: 'pattern',
            pattern: Constants.blobContainerUriPattern,
            errorMessage: format(stringResources.Validator_ErrorMessage_Pattern, containerUriPlaceholder),
        },
        {
            type: 'pattern',
            pattern: `^https://[a-z0-9-]{3,}(\\.blob\\.(${Constants.storageValidEndpointSuffixes})|\\.z[0-9]+\\.blob\\.${Constants.partitionedDnsEndpointSuffix})/([a-z0-9\$](?:[a-z0-9]|(?:\-(?!\-))){1,61}[a-z0-9])/?$|^[*]{10}$`,
            errorMessage: stringResources.Validation_StorageAccountName,
        }
    ],
    isLabelVertical: true
};

const sasTokenPasswordBase: ControlMetadata = {
    type: 'password',
    label: stringResources.ModelName_SasToken,
    isLabelVertical: true,
    placeholder: `${stringResources.Sample}: ?sv=<storage services version>&st=<start time>&se=<expire time>&sr=<resource>&sp=<permissions>&sip=<ip range>&spr=<protocol>&sig=<signature>`,
    description: stringResources.AzureStorage_SasTokenDescription
};

export const fileShareTextareaBase: ControlMetadata = {
    type: 'textbox',
    bind: 'fileShare',
    label: stringResources.FileShare_DisplayText,
    required: true,
    isLabelVertical: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ]
};

// Return metadata according to 'enableFirstClass'
export function parameterizableMetadataBase(
    metadata: ControlMetadata,
    enableFirstClass?: boolean,
    extraParameterizableMetadata?: Partial<IParameterizableMetadata>
): ControlMetadata {
    return enableFirstClass ? {
        ...extraParameterizableMetadata,
        type: 'parameterizable',
        child: metadata
    } : metadata;
}


export function sasUriSetting(isTable: boolean, showFileShare?: boolean, enableFirstClass?: boolean, isGen2 = false): ControlMetadata {
    return ({
        type: 'select-button-switch',
        required: true,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.SasUri,
                value: false
            },
            {
                label: stringResources.AzureKeyVault_DisplayText,
                value: true,
                hideAKVOptionIfNotChosen: false
            }
        ],
        bind: 'enableSasUriAKV',
        isNotRelatedToCredential: true,
        defaultValue: false,
        cases: [
            {
                caseValue: true,
                children: [
                    {
                        type: 'azure-key-vault',
                        bind: 'referedSasUriAKV',
                        isLabelVertical: true,
                        enableAKVParameterization: enableFirstClass
                    },
                    parameterizableMetadataBase(fileShareTextareaBase, enableFirstClass, {
                        hide: !showFileShare
                    })
                ]
            },
            {
                caseValue: false,
                children: [
                    parameterizableMetadataBase(sasUriBase(isTable, showFileShare, isGen2), enableFirstClass, {
                        disableAddParameter: true // parameter is not encryped for sasUri + sasToken case
                    }),
                    {
                        type: 'dynamic-akv-switcher',
                        hideAKVSwitcherInConnStrProps: true,
                        bind: 'sasToken',
                        label: stringResources.ModelName_SasToken,
                        enableAKVParameterization: enableFirstClass,
                        child: parameterizableMetadataBase(sasTokenPasswordBase, enableFirstClass, {
                            attribute: 'sasToken',
                            disableAddParameter: true
                        }),
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureStorage_DisplayText),
                            stringResources.ModelName_SasToken)
                    },
                    parameterizableMetadataBase(fileShareTextareaBase, enableFirstClass, {
                        hide: !showFileShare
                    }),
                    {
                        type: 'private-endpoint-status'
                    }
                ]
            }
        ]
    });
}

export const blobEndpointSetting: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AccountSelectionMethodLabel,
    description: stringResources.AzureStorage_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    isLabelVertical: true,
    defaultValue: '{serviceEndpoint != null || parameterized ? "manual" : "azure"}',
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-storage-account-select',
                    bind: {
                        url: 'serviceEndpoint',
                        scope: '_accountResourceID',
                        kind: 'accountKind'
                    },
                    serviceType: 'blob',
                    showPEStatus: true,
                    fetchAccountKey: false,
                    showAccountKind: true,
                    validators: {
                        url: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable',
                    child: serviceEndpointBase('blob')
                },
                {
                    type: 'select',
                    bind: 'accountKind',
                    label: stringResources.ModelName_StorageAccountKind,
                    options: storageAccountKindOptions,
                    isLabelVertical: true
                },
                {
                    type: 'private-endpoint-status'
                }
            ]
        }
    ]
};

export function storageEndpointSetting(serviceType: PrimaryEndpoints): ControlMetadata {
    return ({
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.AzureStorage_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{serviceEndpoint != null || parameterized ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-storage-account-select',
                        bind: {
                            url: 'serviceEndpoint',
                            scope: '_accountResourceID'
                        },
                        serviceType,
                        showPEStatus: true,
                        fetchAccountKey: false,
                        validators: {
                            url: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    {
                        type: 'parameterizable',
                        child: serviceEndpointBase(serviceType)
                    },
                    {
                        type: 'private-endpoint-status'
                    }
                ]
            }
        ]
    });
}

export const blobContainerUriSetting: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AccountSelectionMethodLabel,
    description: stringResources.AzureStorage_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    isLabelVertical: true,
    defaultValue: '{containerUri != null || parameterized ? "manual" : "azure"}',
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-storage-account-select',
                    bind: {
                        containerUri: 'containerUri'
                    },
                    serviceType: 'blob',
                    showContainer: true,
                    showPEStatus: true,
                    fetchAccountKey: false,
                    validators: {
                        containerUri: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                }]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable',
                    child: containerUriBase
                },
                {
                    type: 'private-endpoint-status'
                }
            ]
        }
    ]
};


export const supportAzureBlobType: string[] = [Metamodel.modelFor(AzureStorageLinkedService).name, Metamodel.modelFor(AzureBlobStorageLinkedService).name];
